import { Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { BlogPost } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';

import { postState } from '../schemas/post-state';

import { PostStateBadge } from './post-state-badge';

const TAGS_SHOWN = 3;

/** Column model of the posts table; `meta` ties columns to the list's sorts and filters. */
export function useBlogPostColumns(storeId: string) {
  const { t } = useTranslation('blog');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<BlogPost>>>(
    () => [
      {
        id: 'title',
        header: t('columns.post'),
        cell: ({ row }) => (
          <div className="min-w-0">
            <Link
              to="/s/$storeId/content/blog/$postId"
              params={{ storeId, postId: row.original.id }}
              className="block truncate font-medium hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {row.original.title}
            </Link>
            <code className="text-muted-foreground block truncate text-xs">
              {row.original.handle}
            </code>
          </div>
        ),
        meta: { label: t('columns.post'), sortField: 'title', lockVisibility: true },
      },
      {
        id: 'category',
        header: t('columns.category'),
        cell: ({ row }) =>
          row.original.category ? (
            <span className="text-sm">{row.original.category.title}</span>
          ) : (
            <span className="text-muted-foreground text-sm">{t('none')}</span>
          ),
        meta: { label: t('columns.category'), filterKey: 'categoryId' },
      },
      {
        id: 'tags',
        header: t('columns.tags'),
        cell: ({ row }) => {
          const tags = row.original.tags;
          return (
            <div className="flex flex-wrap items-center gap-1">
              {tags.slice(0, TAGS_SHOWN).map((tag) => (
                <Badge key={tag} variant="secondary" className="font-normal">
                  {tag}
                </Badge>
              ))}
              {tags.length > TAGS_SHOWN ? (
                <span className="text-muted-foreground text-xs">
                  {t('columns.moreTags', { count: tags.length - TAGS_SHOWN })}
                </span>
              ) : null}
            </div>
          );
        },
        meta: { label: t('columns.tags'), filterKey: 'tag' },
      },
      {
        id: 'status',
        header: t('columns.status'),
        cell: ({ row }) => (
          <PostStateBadge state={postState(row.original)} publishedAt={row.original.publishedAt} />
        ),
        meta: { label: t('columns.status'), filterKey: 'status' },
      },
      {
        id: 'publishedAt',
        header: t('columns.published'),
        cell: ({ row }) =>
          row.original.publishedAt ? (
            <span className="whitespace-nowrap" title={format.utc(row.original.publishedAt)}>
              {format.dateTime(row.original.publishedAt)}
            </span>
          ) : (
            <span className="text-muted-foreground">{t('none')}</span>
          ),
        meta: {
          label: t('columns.published'),
          sortField: 'publishedAt',
          filterKey: 'publishedAt',
        },
      },
      {
        id: 'updatedAt',
        header: t('columns.updated'),
        cell: ({ row }) => (
          <span
            className="text-muted-foreground whitespace-nowrap"
            title={format.utc(row.original.updatedAt)}
          >
            {format.relative(row.original.updatedAt)}
          </span>
        ),
        meta: { label: t('columns.updated'), sortField: 'updatedAt' },
      },
    ],
    [format, storeId, t],
  );
}
